import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  Spinner,
  Alert,
  Button,
  Overlay,
  Tooltip,
} from 'react-bootstrap';
import { axiosGet } from '../../../../utils/axiosGet';
import { useStaticCollections } from '../../../../context/StaticCollections';
import { devError } from '../../../../utils/devLog';

const TIERS = ['d14', 'd7', 'd3', 'd1'];

const TIER_LABELS = {
  d14: '14 days',
  d7: '7 days',
  d3: '3 days',
  d1: '1 day',
};

const TIER_DESCRIPTIONS = {
  d14: 'Reminder sent when 8–14 days remaining',
  d7: 'Reminder sent when 4–7 days remaining',
  d3: 'Reminder sent when 2–3 days remaining',
  d1: 'Reminder sent when 1 day remaining',
};

const SPECIAL_COMMITTEE =
  'Corresponding Membership and Special Election Committee';

const stripTitle = (name, titlesTypesArray) => {
  if (!name) return '';
  const titles = titlesTypesArray.map((t) => t.attributes.name);
  if (titles.length === 0) return name;
  const escaped = titles.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const titlePattern = new RegExp(`^(${escaped.join('|')})\\s+`, 'i');
  return name.replace(titlePattern, '');
};

const TierHeader = ({ tier }) => {
  const [show, setShow] = useState(false);
  const ref = useRef(null);

  return (
    <>
      <th
        ref={ref}
        className="text-center"
        style={{ cursor: 'default' }}
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      >
        {TIER_LABELS[tier]}
      </th>
      <Overlay target={ref.current} show={show} placement="top">
        {(props) => (
          <Tooltip id={`tooltip-${tier}`} {...props}>
            {TIER_DESCRIPTIONS[tier]}
          </Tooltip>
        )}
      </Overlay>
    </>
  );
};

TierHeader.propTypes = {
  tier: PropTypes.oneOf(TIERS).isRequired,
};

const ChairReminderLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { titlesTypesArray } = useStaticCollections();

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await axiosGet('chair-report-reminder-logs');
      setLogs(response.data.data || []);
      setError(null);
    } catch (err) {
      devError('Error fetching reminder logs:', err);
      setError('Failed to load reminder logs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const matrix = logs.reduce((acc, log) => {
    const {
      sectionalCommitteeId,
      sectionalCommitteeName,
      chairName,
      reminderTier,
      emailStatus,
      sentAt,
    } = log.attributes;

    if (!acc[sectionalCommitteeId]) {
      acc[sectionalCommitteeId] = {
        committeeName: sectionalCommitteeName,
        chairName: stripTitle(chairName, titlesTypesArray),
        tiers: {},
      };
    }

    const existing = acc[sectionalCommitteeId].tiers[reminderTier];
    if (!existing || new Date(sentAt) > new Date(existing.sentAt)) {
      acc[sectionalCommitteeId].tiers[reminderTier] = { emailStatus, sentAt };
    }

    return acc;
  }, {});

  const rows = Object.entries(matrix).sort(([, a], [, b]) => {
    if (a.committeeName === SPECIAL_COMMITTEE) return 1;
    if (b.committeeName === SPECIAL_COMMITTEE) return -1;
    return a.committeeName.localeCompare(b.committeeName);
  });

  const renderCell = (tierData) => {
    if (!tierData) {
      return <span className="text-secondary">—</span>;
    }

    const { emailStatus, sentAt } = tierData;
    const date = formatDate(sentAt);
    const isSent = emailStatus === 'sent';

    return (
      <div className="d-flex align-items-center justify-content-center gap-1">
        <i
          className={`bi ${isSent ? 'bi-check-circle-fill text-success' : 'bi-x-circle-fill text-danger'}`}
        />
        <small className={isSent ? 'text-success' : 'text-danger'}>
          {date}
        </small>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  return (
    <div>
      <div className="d-flex justify-content-end mb-3">
        <Button variant="outline-secondary" size="sm" onClick={fetchLogs}>
          <i className="fas fa-sync-alt me-2" />
          Refresh
        </Button>
      </div>

      {rows.length === 0 ? (
        <Alert variant="info">No reminder logs found.</Alert>
      ) : (
        <Table responsive bordered hover size="sm">
          <colgroup>
            <col className="w-25" />
            <col className="w-25" />
            <col className="w-auto" />
            <col className="w-auto" />
            <col className="w-auto" />
            <col className="w-auto" />
          </colgroup>
          <thead className="table-light">
            <tr>
              <th>Committee</th>
              <th>Chair</th>
              {TIERS.map((tier) => (
                <TierHeader key={tier} tier={tier} />
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(([committeeId, { committeeName, chairName, tiers }]) => (
              <tr key={committeeId}>
                <td>{committeeName}</td>
                <td>{chairName}</td>
                {TIERS.map((tier) => (
                  <td key={tier} className="text-center">
                    {renderCell(tiers[tier])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
};

export default ChairReminderLogs;
